import { createContext, useCallback, useContext, useState, type ReactNode } from "react"

export interface PreviewDoc {
  url: string
  title: string
  driveId?: string | null
  itemId?: string | null
}

interface PreviewContextValue {
  doc: PreviewDoc | null
  openPreview: (doc: PreviewDoc) => void
  closePreview: () => void
}

const PreviewContext = createContext<PreviewContextValue | undefined>(undefined)

/** Document preview state — a context because openPreview is called from deep
 *  inside the markdown link renderer as well as from source lists. */
export function PreviewProvider({ children }: { children: ReactNode }) {
  const [doc, setDoc] = useState<PreviewDoc | null>(null)

  const openPreview = useCallback((next: PreviewDoc) => setDoc(next), [])
  const closePreview = useCallback(() => setDoc(null), [])

  return <PreviewContext.Provider value={{ doc, openPreview, closePreview }}>{children}</PreviewContext.Provider>
}

export function usePreview() {
  const ctx = useContext(PreviewContext)
  if (!ctx) throw new Error("usePreview must be used within PreviewProvider")
  return ctx
}
